import { supabase } from '../lib/supabase';
import openai from '../lib/openai';

/**
 * AI-powered recommendation service for personalized content suggestions
 * Analyzes student learning history, progress, and preferences to suggest tailored songs, roles, and musicals
 */
export const recommendationService = {
  /**
   * Rate limiting for OpenAI API calls
   */
  rateLimiter: {
    calls: [],
    maxCallsPerMinute: 10,
    
    canMakeCall() {
      const now = Date.now();
      this.calls = this?.calls?.filter(timestamp => now - timestamp < 60000);
      return this?.calls?.length < this?.maxCallsPerMinute;
    },
    
    recordCall() {
      this?.calls?.push(Date.now());
    },
    
    getWaitTime() {
      if (this?.calls?.length === 0) return 0;
      const oldestCall = Math.min(...this?.calls);
      const waitTime = 60000 - (Date.now() - oldestCall);
      return Math.max(0, waitTime);
    }
  },

  /**
   * Get comprehensive student learning data for AI analysis
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Complete learning history data
   */
  async getStudentLearningData(userId) {
    try {
      // Fetch user profile
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', userId)
        ?.single();

      if (profileError) throw profileError;

      // Fetch lesson progress with song and musical details
      const { data: lessonProgress, error: progressError } = await supabase
        ?.from('user_lesson_progress')
        ?.select(`
          *,
          songs (
            id,
            title,
            key_signature,
            act_number,
            musicals (
              id,
              title,
              genre,
              composer,
              lyricist
            )
          )
        `)
        ?.eq('user_id', userId)
        ?.order('updated_at', { ascending: false })
        ?.limit(50);

      if (progressError) throw progressError;

      // Fetch XP events
      const { data: xpEvents, error: xpError } = await supabase
        ?.from('user_xp_events')
        ?.select(`
          *,
          songs (
            id,
            title,
            musicals (
              title,
              genre
            )
          )
        `)
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(100);

      if (xpError) throw xpError;

      // Fetch quiz attempts with scores
      const { data: quizAttempts, error: quizError } = await supabase
        ?.from('lesson_quiz_attempts')
        ?.select(`
          *,
          lesson_quizzes (
            lesson_section,
            song_id,
            songs (
              title,
              musicals (
                title
              )
            )
          )
        `)
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(30);

      if (quizError) throw quizError;

      // Fetch user favorites
      const { data: songFavorites, error: songFavError } = await supabase
        ?.from('song_favorites')
        ?.select(`
          *,
          songs (
            id,
            title,
            key_signature,
            musicals (
              title,
              genre
            )
          )
        `)
        ?.eq('user_id', userId);

      if (songFavError) throw songFavError;

      const { data: musicalFavorites, error: musicalFavError } = await supabase
        ?.from('user_musical_favorites')
        ?.select(`
          *,
          musicals (
            id,
            title,
            genre,
            composer,
            lyricist
          )
        `)
        ?.eq('user_id', userId);

      if (musicalFavError) throw musicalFavError;

      const { data: roleFavorites, error: roleFavError } = await supabase
        ?.from('user_role_favorites')
        ?.select(`
          *,
          roles (
            id,
            character_name,
            voice_type,
            vocal_range,
            role_type,
            musicals (
              title,
              genre
            )
          )
        `)
        ?.eq('user_id', userId);

      if (roleFavError) throw roleFavError;

      return {
        profile: {
          currentLevel: profile?.current_level,
          totalXp: profile?.total_lifetime_xp,
          dailyStreak: profile?.daily_streak,
          longestStreak: profile?.longest_streak,
          dailyGoalTier: profile?.daily_goal_tier
        },
        lessonProgress: (lessonProgress || [])?.map(lp => ({
          songTitle: lp?.songs?.title,
          musicalTitle: lp?.songs?.musicals?.title,
          genre: lp?.songs?.musicals?.genre,
          lessonSection: lp?.lesson_section,
          lessonStatus: lp?.lesson_status,
          quizCompleted: lp?.quiz_completed,
          quizScore: lp?.quiz_score,
          isQuizPerfect: lp?.is_quiz_perfect,
          videoProgress: parseFloat(lp?.video_progress_percentage) || 0,
          completedAt: lp?.completed_at,
          timesRepeated: lp?.times_repeated
        })),
        xpEvents: (xpEvents || [])?.map(xp => ({
          source: xp?.source,
          amount: xp?.amount,
          songTitle: xp?.songs?.title,
          musicalTitle: xp?.songs?.musicals?.title,
          genre: xp?.songs?.musicals?.genre,
          lessonSection: xp?.lesson_section,
          createdAt: xp?.created_at
        })),
        quizAttempts: (quizAttempts || [])?.map(qa => ({
          score: qa?.score,
          isPerfect: qa?.is_perfect,
          songTitle: qa?.lesson_quizzes?.songs?.title,
          musicalTitle: qa?.lesson_quizzes?.songs?.musicals?.title,
          lessonSection: qa?.lesson_quizzes?.lesson_section,
          createdAt: qa?.created_at
        })),
        favorites: {
          songs: (songFavorites || [])?.map(sf => ({
            title: sf?.songs?.title,
            musicalTitle: sf?.songs?.musicals?.title,
            genre: sf?.songs?.musicals?.genre,
            keySignature: sf?.songs?.key_signature
          })),
          musicals: (musicalFavorites || [])?.map(mf => ({
            title: mf?.musicals?.title,
            genre: mf?.musicals?.genre,
            composer: mf?.musicals?.composer,
            lyricist: mf?.musicals?.lyricist
          })),
          roles: (roleFavorites || [])?.map(rf => ({
            characterName: rf?.roles?.character_name,
            voiceType: rf?.roles?.voice_type,
            vocalRange: rf?.roles?.vocal_range,
            roleType: rf?.roles?.role_type,
            musicalTitle: rf?.roles?.musicals?.title,
            genre: rf?.roles?.musicals?.genre
          }))
        }
      };
    } catch (error) {
      console.error('Error fetching student learning data:', error);
      throw error;
    }
  },

  /**
   * Get AI-powered personalized song recommendations
   * @param {string} userId - User ID
   * @param {number} limit - Number of recommendations to return
   * @returns {Promise<Object>} Personalized song recommendations with reasoning
   */
  async getPersonalizedSongRecommendations(userId, limit = 5) {
    try {
      // Check API key availability
      const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
      if (!apiKey || apiKey === 'your-openai-api-key-here' || apiKey?.trim() === '') {
        console.warn('OpenAI API key not configured, using fallback recommendations');
        return await this?.getFallbackSongRecommendations(userId, limit);
      }

      // Check rate limiter
      if (!this?.rateLimiter?.canMakeCall()) {
        const waitTime = this?.rateLimiter?.getWaitTime();
        console.warn(`Rate limit reached. Using fallback recommendations.`);
        return await this?.getFallbackSongRecommendations(userId, limit);
      }

      // Get student learning data
      const learningData = await this?.getStudentLearningData(userId);

      // Get available songs for recommendations
      const { data: availableSongs, error: songsError } = await supabase
        ?.from('songs')
        ?.select(`
          id,
          title,
          key_signature,
          act_number,
          musicals (
            title,
            genre,
            composer
          )
        `)
        ?.limit(100);

      if (songsError) throw songsError;

      // Prepare AI prompt
      const prompt = this?.buildSongRecommendationPrompt(learningData, availableSongs, limit);

      // Call OpenAI with timeout and error handling
      this?.rateLimiter?.recordCall();
      
      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('OpenAI API request timeout')), 15000)
      );
      
      const apiPromise = openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        max_tokens: 1500,
        temperature: 0.7,
        messages: [
          {
            role: 'system',
            content: `You are an expert musical theater education consultant specializing in personalized learning pathways. Analyze student data to recommend songs that match their skill level, interests, and learning patterns. Consider their completed content, performance metrics, favorite genres, and voice characteristics. Always return valid JSON only.`
          },
          {
            role: 'user',
            content: prompt
          }
        ]
      });

      const response = await Promise.race([apiPromise, timeoutPromise]);

      // Parse AI response
      const content = response?.choices?.[0]?.message?.content;
      const jsonMatch = content?.match(/\{[\s\S]*\}/);
      const recommendations = jsonMatch ? JSON.parse(jsonMatch?.[0]) : JSON.parse(content);

      return {
        recommendations: recommendations?.songs || [],
        reasoning: recommendations?.analysis || 'AI-powered recommendations based on your learning history',
        generatedWithAI: true,
        timestamp: new Date()?.toISOString()
      };
    } catch (error) {
      // Log specific error details for debugging
      if (error?.message?.includes('timeout')) {
        console.warn('OpenAI API request timed out, using fallback recommendations');
      } else if (error?.message?.includes('Connection error') || error?.code === 'ENOTFOUND' || error?.code === 'ECONNREFUSED') {
        console.warn('Network connectivity issue with OpenAI API, using fallback recommendations');
      } else if (error?.status === 401) {
        console.warn('OpenAI API authentication failed - please check your API key');
      } else if (error?.status === 429) {
        console.warn('OpenAI API rate limit exceeded, using fallback recommendations');
      } else {
        console.error('Error generating AI song recommendations:', error?.message || error);
      }
      
      return await this?.getFallbackSongRecommendations(userId, limit);
    }
  },

  /**
   * Get AI-powered personalized role recommendations
   * @param {string} userId - User ID
   * @param {number} limit - Number of recommendations to return
   * @returns {Promise<Object>} Personalized role recommendations with reasoning
   */
  async getPersonalizedRoleRecommendations(userId, limit = 5) {
    try {
      // Check API key availability
      const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
      if (!apiKey || apiKey === 'your-openai-api-key-here' || apiKey?.trim() === '') {
        console.warn('OpenAI API key not configured, using fallback recommendations');
        return await this?.getFallbackRoleRecommendations(userId, limit);
      }

      // Check rate limiter
      if (!this?.rateLimiter?.canMakeCall()) {
        const waitTime = this?.rateLimiter?.getWaitTime();
        console.warn(`Rate limit reached. Using fallback recommendations.`);
        return await this?.getFallbackRoleRecommendations(userId, limit);
      }

      // Get student learning data
      const learningData = await this?.getStudentLearningData(userId);

      // Get available roles for recommendations
      const { data: availableRoles, error: rolesError } = await supabase
        ?.from('roles')
        ?.select(`
          id,
          character_name,
          voice_type,
          vocal_range,
          role_type,
          age_range,
          description,
          musicals (
            title,
            genre,
            composer
          )
        `)
        ?.limit(50);

      if (rolesError) throw rolesError;

      // Prepare AI prompt
      const prompt = this?.buildRoleRecommendationPrompt(learningData, availableRoles, limit);

      // Call OpenAI with timeout and error handling
      this?.rateLimiter?.recordCall();
      
      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('OpenAI API request timeout')), 15000)
      );
      
      const apiPromise = openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        max_tokens: 1500,
        temperature: 0.7,
        messages: [
          {
            role: 'system',
            content: `You are an expert musical theater casting director and vocal coach. Analyze student performance data to recommend roles that match their vocal type, skill progression, and interests. Consider their favorite genres, completed songs, voice characteristics, and performance patterns. Always return valid JSON only.`
          },
          {
            role: 'user',
            content: prompt
          }
        ]
      });

      const response = await Promise.race([apiPromise, timeoutPromise]);

      // Parse AI response
      const content = response?.choices?.[0]?.message?.content;
      const jsonMatch = content?.match(/\{[\s\S]*\}/);
      const recommendations = jsonMatch ? JSON.parse(jsonMatch?.[0]) : JSON.parse(content);

      return {
        recommendations: recommendations?.roles || [],
        reasoning: recommendations?.analysis || 'AI-powered role recommendations based on your vocal characteristics and learning patterns',
        generatedWithAI: true,
        timestamp: new Date()?.toISOString()
      };
    } catch (error) {
      // Log specific error details for debugging
      if (error?.message?.includes('timeout')) {
        console.warn('OpenAI API request timed out, using fallback recommendations');
      } else if (error?.message?.includes('Connection error') || error?.code === 'ENOTFOUND' || error?.code === 'ECONNREFUSED') {
        console.warn('Network connectivity issue with OpenAI API, using fallback recommendations');
      } else if (error?.status === 401) {
        console.warn('OpenAI API authentication failed - please check your API key');
      } else if (error?.status === 429) {
        console.warn('OpenAI API rate limit exceeded, using fallback recommendations');
      } else {
        console.error('Error generating AI role recommendations:', error?.message || error);
      }
      
      return await this?.getFallbackRoleRecommendations(userId, limit);
    }
  },

  /**
   * Get AI-powered personalized musical recommendations
   * @param {string} userId - User ID
   * @param {number} limit - Number of recommendations to return
   * @returns {Promise<Object>} Personalized musical recommendations with reasoning
   */
  async getPersonalizedMusicalRecommendations(userId, limit = 5) {
    try {
      // Check API key availability
      const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
      if (!apiKey || apiKey === 'your-openai-api-key-here' || apiKey?.trim() === '') {
        console.warn('OpenAI API key not configured, using fallback recommendations');
        return await this?.getFallbackMusicalRecommendations(userId, limit);
      }

      // Check rate limiter
      if (!this?.rateLimiter?.canMakeCall()) {
        const waitTime = this?.rateLimiter?.getWaitTime();
        console.warn(`Rate limit reached. Using fallback recommendations.`);
        return await this?.getFallbackMusicalRecommendations(userId, limit);
      }

      // Get student learning data
      const learningData = await this?.getStudentLearningData(userId);

      // Get available musicals for recommendations
      const { data: availableMusicals, error: musicalsError } = await supabase
        ?.from('musicals')
        ?.select('id, title, genre, composer, lyricist, year_premiered, description')
        ?.eq('is_active', true)
        ?.limit(50);

      if (musicalsError) throw musicalsError;

      // Prepare AI prompt
      const prompt = this?.buildMusicalRecommendationPrompt(learningData, availableMusicals, limit);

      // Call OpenAI with timeout and error handling
      this?.rateLimiter?.recordCall();
      
      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('OpenAI API request timeout')), 15000)
      );
      
      const apiPromise = openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        max_tokens: 1500,
        temperature: 0.7,
        messages: [
          {
            role: 'system',
            content: `You are an expert musical theater historian and education curator. Analyze student learning patterns to recommend complete musicals that align with their interests, skill progression, and favorite genres. Consider their completed shows, performance metrics, and thematic preferences. Always return valid JSON only.`
          },
          {
            role: 'user',
            content: prompt
          }
        ]
      });

      const response = await Promise.race([apiPromise, timeoutPromise]);

      // Parse AI response
      const content = response?.choices?.[0]?.message?.content;
      const jsonMatch = content?.match(/\{[\s\S]*\}/);
      const recommendations = jsonMatch ? JSON.parse(jsonMatch?.[0]) : JSON.parse(content);

      return {
        recommendations: recommendations?.musicals || [],
        reasoning: recommendations?.analysis || 'AI-powered musical recommendations based on your interests and learning journey',
        generatedWithAI: true,
        timestamp: new Date()?.toISOString()
      };
    } catch (error) {
      // Log specific error details for debugging
      if (error?.message?.includes('timeout')) {
        console.warn('OpenAI API request timed out, using fallback recommendations');
      } else if (error?.message?.includes('Connection error') || error?.code === 'ENOTFOUND' || error?.code === 'ECONNREFUSED') {
        console.warn('Network connectivity issue with OpenAI API, using fallback recommendations');
      } else if (error?.status === 401) {
        console.warn('OpenAI API authentication failed - please check your API key');
      } else if (error?.status === 429) {
        console.warn('OpenAI API rate limit exceeded, using fallback recommendations');
      } else {
        console.error('Error generating AI musical recommendations:', error?.message || error);
      }
      
      return await this?.getFallbackMusicalRecommendations(userId, limit);
    }
  },

  /**
   * Build AI prompt for song recommendations
   * @param {Object} learningData - Student learning data
   * @param {Array} availableSongs - Available songs for recommendations
   * @param {number} limit - Number of recommendations
   * @returns {string} Formatted prompt
   */
  buildSongRecommendationPrompt(learningData, availableSongs, limit) {
    const completedSongs = learningData?.lessonProgress
      ?.filter(lp => lp?.lessonStatus === 'completed')
      ?.map(lp => `${lp?.songTitle} from ${lp?.musicalTitle}`);

    const favoriteGenres = [...new Set(learningData?.favorites?.songs?.map(s => s?.genre))];
    const averageQuizScore = learningData?.quizAttempts?.reduce((sum, qa) => sum + qa?.score, 0) / (learningData?.quizAttempts?.length || 1);

    return `Analyze this student's learning data and recommend ${limit} songs that match their skill level and interests:

STUDENT PROFILE:
- Current Level: ${learningData?.profile?.currentLevel}
- Total XP: ${learningData?.profile?.totalXp}
- Daily Streak: ${learningData?.profile?.dailyStreak} days
- Average Quiz Score: ${Math.round(averageQuizScore)}%

COMPLETED SONGS (${completedSongs?.length}):
${completedSongs?.slice(0, 10)?.join(', ')}

FAVORITE GENRES:
${favoriteGenres?.join(', ') || 'Not specified'}

FAVORITE SONGS:
${learningData?.favorites?.songs?.slice(0, 5)?.map(s => `${s?.title} from ${s?.musicalTitle}`)?.join(', ')}

PERFORMANCE PATTERNS:
- Perfect Quizzes: ${learningData?.quizAttempts?.filter(qa => qa?.isPerfect)?.length} / ${learningData?.quizAttempts?.length}
- Repeated Lessons: ${learningData?.lessonProgress?.filter(lp => lp?.timesRepeated > 0)?.length}

AVAILABLE SONGS TO CHOOSE FROM:
${availableSongs?.slice(0, 50)?.map(s => `${s?.title} from ${s?.musicals?.title} (${s?.musicals?.genre})`)?.join(', ')}

Return JSON with this structure:
{
  "songs": [
    {
      "title": "Song Name",
      "musical": "Musical Name",
      "reason": "Why this matches the student's profile",
      "skillMatch": "beginner/intermediate/advanced",
      "confidenceScore": 0.0-1.0
    }
  ],
  "analysis": "Overall recommendation strategy"
}`;
  },

  /**
   * Build AI prompt for role recommendations
   * @param {Object} learningData - Student learning data
   * @param {Array} availableRoles - Available roles for recommendations
   * @param {number} limit - Number of recommendations
   * @returns {string} Formatted prompt
   */
  buildRoleRecommendationPrompt(learningData, availableRoles, limit) {
    const favoriteVoiceTypes = [...new Set(learningData?.favorites?.roles?.map(r => r?.voiceType))];
    const favoriteRoleTypes = [...new Set(learningData?.favorites?.roles?.map(r => r?.roleType))];

    return `Analyze this student's vocal characteristics and learning patterns to recommend ${limit} roles:

STUDENT PROFILE:
- Current Level: ${learningData?.profile?.currentLevel}
- Total XP: ${learningData?.profile?.totalXp}

VOCAL PREFERENCES:
- Favorite Voice Types: ${favoriteVoiceTypes?.join(', ') || 'Not specified'}
- Favorite Role Types: ${favoriteRoleTypes?.join(', ') || 'Not specified'}

FAVORITE ROLES:
${learningData?.favorites?.roles?.slice(0, 5)?.map(r => `${r?.characterName} (${r?.voiceType}) from ${r?.musicalTitle}`)?.join(', ')}

COMPLETED CONTENT:
- Songs Completed: ${learningData?.lessonProgress?.filter(lp => lp?.lessonStatus === 'completed')?.length}
- Favorite Genres: ${[...new Set(learningData?.favorites?.songs?.map(s => s?.genre))]?.join(', ')}

AVAILABLE ROLES TO CHOOSE FROM:
${availableRoles?.slice(0, 30)?.map(r => `${r?.character_name} (${r?.voice_type}, ${r?.role_type}) from ${r?.musicals?.title}`)?.join(', ')}

Return JSON with this structure:
{
  "roles": [
    {
      "characterName": "Character Name",
      "musical": "Musical Name",
      "voiceType": "Voice type",
      "reason": "Why this role matches the student",
      "skillLevel": "beginner/intermediate/advanced",
      "confidenceScore": 0.0-1.0
    }
  ],
  "analysis": "Overall casting recommendation strategy"
}`;
  },

  /**
   * Build AI prompt for musical recommendations
   * @param {Object} learningData - Student learning data
   * @param {Array} availableMusicals - Available musicals for recommendations
   * @param {number} limit - Number of recommendations
   * @returns {string} Formatted prompt
   */
  buildMusicalRecommendationPrompt(learningData, availableMusicals, limit) {
    const completedMusicals = [...new Set(learningData?.lessonProgress
      ?.filter(lp => lp?.lessonStatus === 'completed')
      ?.map(lp => lp?.musicalTitle))];

    return `Analyze this student's learning history to recommend ${limit} complete musicals:

STUDENT PROFILE:
- Current Level: ${learningData?.profile?.currentLevel}
- Total XP: ${learningData?.profile?.totalXp}

COMPLETED MUSICALS:
${completedMusicals?.join(', ')}

FAVORITE MUSICALS:
${learningData?.favorites?.musicals?.map(m => `${m?.title} (${m?.genre})`)?.join(', ')}

FAVORITE GENRES:
${[...new Set(learningData?.favorites?.musicals?.map(m => m?.genre))]?.join(', ')}

LEARNING PATTERNS:
- Songs Completed: ${learningData?.lessonProgress?.filter(lp => lp?.lessonStatus === 'completed')?.length}
- Active Days Streak: ${learningData?.profile?.dailyStreak}

AVAILABLE MUSICALS TO CHOOSE FROM:
${availableMusicals?.slice(0, 30)?.map(m => `${m?.title} (${m?.genre}) by ${m?.composer || 'Unknown'}`)?.join(', ')}

Return JSON with this structure:
{
  "musicals": [
    {
      "title": "Musical Name",
      "genre": "Genre",
      "reason": "Why this musical matches the student's interests",
      "explorationValue": "What new learning opportunities this offers",
      "confidenceScore": 0.0-1.0
    }
  ],
  "analysis": "Overall musical recommendation strategy"
}`;
  },

  /**
   * Fallback song recommendations when AI is unavailable
   * @param {string} userId - User ID
   * @param {number} limit - Number of recommendations
   * @returns {Promise<Object>} Basic recommendations
   */
  async getFallbackSongRecommendations(userId, limit) {
    try {
      const learningData = await this?.getStudentLearningData(userId);
      
      // Get completed song IDs to exclude
      const completedSongIds = learningData?.lessonProgress
        ?.filter(lp => lp?.lessonStatus === 'completed')
        ?.map(lp => lp?.songs?.id)
        ?.filter(Boolean);

      // Get songs from favorite genres
      const favoriteGenres = [...new Set(learningData?.favorites?.musicals?.map(m => m?.genre))];
      
      const { data: songs, error } = await supabase
        ?.from('songs')
        ?.select(`
          id,
          title,
          key_signature,
          musicals (
            title,
            genre,
            poster_url
          )
        `)
        ?.not('id', 'in', `(${completedSongIds?.join(',')})`)
        ?.limit(limit * 2);

      if (error) throw error;

      // Filter by favorite genres if available
      let filteredSongs = songs;
      if (favoriteGenres?.length > 0) {
        filteredSongs = songs?.filter(s => favoriteGenres?.includes(s?.musicals?.genre));
      }

      return {
        recommendations: filteredSongs?.slice(0, limit)?.map(s => ({
          id: s?.id,
          title: s?.title,
          musical: s?.musicals?.title,
          genre: s?.musicals?.genre,
          posterUrl: s?.musicals?.poster_url,
          reason: 'Recommended based on your favorite genres',
          skillMatch: 'intermediate',
          confidenceScore: 0.6
        })),
        reasoning: 'Basic recommendations based on your favorite genres and uncompleted content',
        generatedWithAI: false,
        timestamp: new Date()?.toISOString()
      };
    } catch (error) {
      console.error('Error generating fallback song recommendations:', error);
      return {
        recommendations: [],
        reasoning: 'Unable to generate recommendations at this time',
        generatedWithAI: false,
        timestamp: new Date()?.toISOString()
      };
    }
  },

  /**
   * Fallback role recommendations when AI is unavailable
   * @param {string} userId - User ID
   * @param {number} limit - Number of recommendations
   * @returns {Promise<Object>} Basic recommendations
   */
  async getFallbackRoleRecommendations(userId, limit) {
    try {
      const learningData = await this?.getStudentLearningData(userId);
      
      // Get favorite voice types
      const favoriteVoiceTypes = [...new Set(learningData?.favorites?.roles?.map(r => r?.voiceType))];
      
      const { data: roles, error } = await supabase
        ?.from('roles')
        ?.select(`
          id,
          character_name,
          voice_type,
          vocal_range,
          role_type,
          musicals (
            title,
            genre,
            poster_url
          )
        `)
        ?.eq('is_principal', true)
        ?.limit(limit * 2);

      if (error) throw error;

      // Filter by favorite voice types if available
      let filteredRoles = roles;
      if (favoriteVoiceTypes?.length > 0) {
        filteredRoles = roles?.filter(r => favoriteVoiceTypes?.includes(r?.voice_type));
      }

      return {
        recommendations: filteredRoles?.slice(0, limit)?.map(r => ({
          id: r?.id,
          characterName: r?.character_name,
          musical: r?.musicals?.title,
          voiceType: r?.voice_type,
          vocalRange: r?.vocal_range,
          roleType: r?.role_type,
          posterUrl: r?.musicals?.poster_url,
          reason: 'Recommended based on your vocal preferences',
          skillLevel: 'intermediate',
          confidenceScore: 0.6
        })),
        reasoning: 'Basic role recommendations based on your vocal type preferences',
        generatedWithAI: false,
        timestamp: new Date()?.toISOString()
      };
    } catch (error) {
      console.error('Error generating fallback role recommendations:', error);
      return {
        recommendations: [],
        reasoning: 'Unable to generate recommendations at this time',
        generatedWithAI: false,
        timestamp: new Date()?.toISOString()
      };
    }
  },

  /**
   * Fallback musical recommendations when AI is unavailable
   * @param {string} userId - User ID
   * @param {number} limit - Number of recommendations
   * @returns {Promise<Object>} Basic recommendations
   */
  async getFallbackMusicalRecommendations(userId, limit) {
    try {
      const learningData = await this?.getStudentLearningData(userId);
      
      // Get favorite genres
      const favoriteGenres = [...new Set(learningData?.favorites?.musicals?.map(m => m?.genre))];
      
      // Get completed musical IDs to exclude
      const completedMusicalIds = [...new Set(learningData?.lessonProgress
        ?.filter(lp => lp?.lessonStatus === 'completed')
        ?.map(lp => lp?.songs?.musicals?.id)
        ?.filter(Boolean))];

      const { data: musicals, error } = await supabase
        ?.from('musicals')
        ?.select('id, title, genre, composer, lyricist, poster_url, description')
        ?.eq('is_active', true)
        ?.not('id', 'in', `(${completedMusicalIds?.join(',')})`)
        ?.limit(limit * 2);

      if (error) throw error;

      // Filter by favorite genres if available
      let filteredMusicals = musicals;
      if (favoriteGenres?.length > 0) {
        filteredMusicals = musicals?.filter(m => favoriteGenres?.includes(m?.genre));
      }

      return {
        recommendations: filteredMusicals?.slice(0, limit)?.map(m => ({
          id: m?.id,
          title: m?.title,
          genre: m?.genre,
          composer: m?.composer,
          lyricist: m?.lyricist,
          posterUrl: m?.poster_url,
          reason: 'Recommended based on your favorite genres',
          explorationValue: 'Discover new songs and roles in this production',
          confidenceScore: 0.6
        })),
        reasoning: 'Basic musical recommendations based on your genre preferences',
        generatedWithAI: false,
        timestamp: new Date()?.toISOString()
      };
    } catch (error) {
      console.error('Error generating fallback musical recommendations:', error);
      return {
        recommendations: [],
        reasoning: 'Unable to generate recommendations at this time',
        generatedWithAI: false,
        timestamp: new Date()?.toISOString()
      };
    }
  }
};

export default recommendationService;